import { useState, useRef, useEffect } from "react";
import DownArrow from "../../../../../assets/icons/down-arrow.svg";
import PropTypes from "prop-types";
import "./AccessibleDropdown.css";

function getNextHighlightedIndex(prev, optionsLength) {
  if (prev < optionsLength - 1) {
    return prev + 1;
  }
  if (prev === optionsLength - 1) {
    return 0;
  }
  return prev;
}

function getPrevHighlightedIndex(prev, optionsLength) {
  if (prev > 0) {
    return prev - 1;
  }
  if (prev === 0) {
    return optionsLength - 1;
  }
  return prev;
}

function AccessibleDropdown({ options, value, onChange, label, labelId, id, placeholder = "Select an option", isDisabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const touchStartPos = useRef({ x: 0, y: 0 });
  const isScrolling = useRef(false);
  const scrollThreshold = 10;

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    isScrolling.current = false;
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

    if (deltaY > scrollThreshold || deltaX > scrollThreshold) {
      isScrolling.current = true;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(0);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      scrollIntoViewHandler(highlightedIndex);
    }
  }, [isOpen, highlightedIndex]);

  const scrollIntoViewHandler = (index, block = "nearest") => {
    const highlightedElement = listRef.current?.children[index];
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block });
    }
  };

  const handleSelect = (option, index) => {
    onChange(option);
    setIsOpen(false);
    const mainIndex = options.findIndex((opt) => opt.label === option.label);
    setSelectedIndex(mainIndex);
    setHighlightedIndex(0);
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " " || event.key === "touchstart") {
        let mainIndex = selectedIndex;
        if (mainIndex === -1 && value?.label) {
          mainIndex = options.findIndex((opt) => opt.label === value?.label);
          setSelectedIndex(mainIndex);
        }
        setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
        setTimeout(() => {
          mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
        }, 0);
      }
      return;
    }

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex], highlightedIndex);
        }
        break;
      case "Escape":
      case "Tab":
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex(0);
        setIsOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => getNextHighlightedIndex(prev, options.length));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => getPrevHighlightedIndex(prev, options.length));
        break;
      default:
        break;
    }
  };

  const onClickHandler = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      let mainIndex = selectedIndex;
      if (mainIndex === -1 && value?.label) {
        mainIndex = options.findIndex((opt) => opt.label === value?.label);
        setSelectedIndex(mainIndex);
      }
      setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
      setTimeout(() => {
        mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
      }, 0);
    }
    buttonRef.current?.focus();
  };

  const onFocusHandler = (e) => {
    e.preventDefault();
    setIsFocused(true);
  };

  const onBlurHandler = () => {
    setIsFocused(false);
  };

  const onTouchEndHandler = (e) => {
    e.preventDefault();
    buttonRef.current.click();
  };

  const optionOnTouchEndHandler = (e, option, index) => {
    e.preventDefault();
    if (!isScrolling.current) {
      handleSelect(option, index);
    }
  };

  return (
    <>
      {label && (
        <label htmlFor={id} className="block text-lg font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div
        ref={dropdownRef}
        className="dropdown-container"

      >
        <button
          tabIndex={0}
          ref={buttonRef}
          id={id}
          type="button"
          className={`dropdown-button ${isFocused ? "dropdown-button-focused" : ""}`}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
          aria-activedescendant={highlightedIndex >= 0 && options[highlightedIndex] ? `${id}-option-${options[highlightedIndex]?.value}` : undefined}
          onClick={onClickHandler}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onTouchEnd={onTouchEndHandler}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
        >
          <span className={value?.label ? "dropdown-button-text" : "dropdown-button-placeholder"}>{value?.label || placeholder}</span>
          <img src={DownArrow} alt="" aria-hidden="true" className={`dropdown-arrow ${isOpen ? "dropdown-arrow-open" : ""}`} />
        </button>
        {isOpen && (
          <ul
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            className="dropdown-list"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={`${id}-option-${option.value}`}
                role="option"
                tabIndex={0}
                aria-selected={value?.value === option.value}
                aria-label={option.label}
                className={`
                dropdown-option
                ${index === highlightedIndex ? "dropdown-option-highlighted" : ""}
                ${value?.value === option.value ? "dropdown-option-selected" : ""}
              `}
                onClick={() => handleSelect(option, index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(option, index);
                  }
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                onTouchEnd={(e) => optionOnTouchEndHandler(e, option, index)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

AccessibleDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  value: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  labelId: PropTypes.string,
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default AccessibleDropdown;